import React, { useState } from 'react';
import AiSparklesIcon from 'components/general/ai_sparkles_icon/ai_sparkles_icon';
import InfoIcon from 'components/general/info_icon/info_icon';
import ExtractionModal from './extraction_modal';
import FileUploadSection from './file_upload_section';
import TabSelector from './tab_selector';
import Tooltip from 'components/general/tooltip/tooltip';

const styles = require('./ai_assist.module.scss');

interface AiAssistProps {
  extractPath: string;
  extractFilePath: string;
}

const MIN_TEXT_LENGTH = 1;

export default function AiAssist({ extractPath, extractFilePath }: AiAssistProps) {
  const [activeTab, setActiveTab] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isTextButtonEnabled = textContent.trim().length >= MIN_TEXT_LENGTH;
  const isFileButtonEnabled = selectedFile !== null;
  const isButtonEnabled = activeTab === 'text' ? isTextButtonEnabled : isFileButtonEnabled;

  const handleFileSelect = (file: File) => {
    setIsFileLoading(true);
    setTimeout(() => {
      setSelectedFile(file);
      setIsFileLoading(false);
    }, 500);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isButtonEnabled || isLoading) {
      return;
    }

    setIsLoading(true);

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';

    try {
      let response: Response;

      if (activeTab === 'text') {
        response = await fetch(extractPath, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ job_text: textContent }),
        });
      } else {
        const formData = new FormData();
        formData.append('file', selectedFile as File);

        response = await fetch(extractFilePath, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
          },
          body: formData,
        });
      }

      const data = await response.json();

      if (response.ok) {
        window.location.href = data.redirect_url;
      } else {
        window.Alerts.alert(data.error || 'An unexpected error occurred. Please try again.');
        setIsLoading(false);
      }
    } catch {
      window.Alerts.alert('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const placeholderText = `Write or paste your job information here. It can be in any language.

You can include:
• Job title
• Required skills and qualifications
• Responsibilities
• Benefits and perks
• Location and work model

Don't worry about formatting. AI will organize everything for you.`;

  const renderTextInputSection = () => (
    <div className={styles.inputSection}>
      <div className={styles.inputHeader}>
        <div className={styles.labelRow}>
          <label htmlFor="jobDescription" className={styles.label}>
            Share your job description
          </label>
          <Tooltip
            text="AI extracts and prefills job details such as title, description, and skills. You can review and fill in what's missing after extraction."
          >
            <button type="button" className={styles.infoIcon} aria-label="Information">
              <InfoIcon width="18" height="18" />
            </button>
          </Tooltip>
        </div>
        <p className={styles.inputDescription}>
          Include all your job details (any language). Our AI will generate the final job post in English.
        </p>
      </div>

      <textarea
        id="jobDescription"
        className={styles.textarea}
        placeholder={placeholderText}
        value={textContent}
        onChange={(e) => setTextContent(e.target.value)}
        disabled={isLoading}
      />
    </div>
  );

  const getTooltipText = () => {
    if (activeTab === 'text') {
      return 'Please share a job description';
    }
    return 'Please upload a file';
  };

  return (
    <>
      <ExtractionModal isOpen={isLoading} />
      <div className={styles.pageWrapper}>
        <div className={styles.contentCard}>
          <div className={styles.aiAssistContainer}>
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <AiSparklesIcon className={styles.icon} width="20" height="20" />
                <h1 className={styles.title}>Post a Job - AI Assisted</h1>
              </div>
              <p className={styles.subtitle}>Choose how you want to share your job information.</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.content}>
                <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />

                {activeTab === 'text' && renderTextInputSection()}

                {activeTab === 'file' && (
                  <FileUploadSection
                    selectedFile={selectedFile}
                    isFileLoading={isFileLoading}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove}
                    isDisabled={isLoading}
                  />
                )}

                {!isButtonEnabled && !isLoading ? (
                  <Tooltip
                    text={getTooltipText()}
                    className={styles.tooltipFullWidth}
                    style={{
                      left: '50%',
                      marginLeft: '-5.625rem',
                    }}
                  >
                    <button
                      type="submit"
                      className={styles.extractButton}
                      disabled={true}
                    >
                      <AiSparklesIcon className={styles.buttonIcon} width="20" height="20" fill="currentColor" />
                      Extract job information
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    type="submit"
                    className={`${styles.extractButton} ${isButtonEnabled && !isLoading ? styles.extractButtonEnabled : ''
                      }`}
                    disabled={!isButtonEnabled || isLoading}
                  >
                    <AiSparklesIcon className={styles.buttonIcon} width="20" height="20" fill="currentColor" />
                    {isLoading ? 'Extracting...' : 'Extract job information'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
